// Documentation here:
// https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/

import { IUser } from '~/src/dash/api/console-api';
import { noop } from '~/src/dash/utils/noop';
import { hasRoleSuperUser, isSuper } from '~/src/dash/utils/roles';
import { getEnv } from '~/src/dash/resellers/utils/importResellerConfig';

// FOR NOW, ALL SUPER USERS HAVE SEGMENT/MIXPANEL DATA DISABLED

function getAnalytics() {
  // @ts-ignore
  return (
    window.analytics || {
      load: noop,
      page: noop,
      track: noop,
      identify: noop,
    }
  );
}

export const loadAnalytics = () => {
  getAnalytics().load('yY3x7w3ZDNCT5jCI8sqETkpLvgKxOCzU');
};

// ids to track events even though they are super users
const superUserOverrideList = ['747', '336'];

export const page = (title: string, path: string, user: IUser) => {
  if (!user || (hasRoleSuperUser(user) && !superUserOverrideList.includes(user.ID))) {
    return;
  }
  getAnalytics().page(title, {
    title,
    path,
  });
};

export const track = (
  name: string,
  properties: unknown,
  user: IUser,
  allowedSuperUserIDs: string[] = [],
): void => {
  if (getEnv() !== 'prod') return; // only track in prod
  const isAllowedSuperUser =
    superUserOverrideList.includes(user.ID) || allowedSuperUserIDs.includes(user.ID);
  if (!user || (hasRoleSuperUser(user) && !isAllowedSuperUser)) {
    return;
  }
  getAnalytics().track(name, properties);
};

export const identify = (publisherID: string, user: IUser, resellerID: number) => {
  if (!user) return;
  getAnalytics().identify(user.ID, {
    Name: `${user.FirstName} ${user.LastName}`,
    Email: user.Username,
    publisherID,
    is_reseller_publica: resellerID === -1,
    is_super_user: isSuper(user),
  });
};

// we also track this for SU as it affect everyone!
export const track_api_latency = (
  full_url: string,
  latency_api: number,
  latency_parse_response: number,
  library_used: string,
) => {
  if (latency_api === 0) {
    return;
  }

  const url_path = getPathFromURL(full_url);
  if (url_path === '') {
    return;
  }

  // console.warn('api_latency: ', url_path, ' latency_api: ', latency_api, 'latency_parse: ', latency_parse_response, ' library used: ', library_used);

  return;

  getAnalytics().track('api_latency', {
    route: url_path,
    latency_api_ms: latency_api,
    latency_parse_response_ms: latency_parse_response,
    http_library_used: library_used,
  });
};

// input: https://api.publica.dev/v1/settings/channel_groups?publisher…ui_fields=&access_token=abc
// output: /v1/settings/channel_groups
export const getPathFromURL = (full_url: string): string => {
  const match =
    full_url.match(
      /^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/,
    ) || '';
  if (match.length > 6) {
    return match[5];
  }
  return '';
};

export const cleanCommaSeparatedString = (str: string): string[] => {
  const result = [] as string[];
  if (str === '') {
    return result;
  }
  for (let part of str.split(',')) {
    part = part.replace(/'/g, '');
    result.push(part);
  }

  return result;
};

export type ReportingEventTypes =
  | 'Report - Preview Report'
  | 'Report - Send Email'
  | 'Report - Send Now'
  | 'Report - Save Report'
  | 'Report - Create Report'
  | 'Report - Download Report'
  | 'Report - Schedule Report'
  | 'Report Preview - Preview Loaded'
  | 'Report Preview - Download Report'
  | 'Report Preview - Refresh'
  | 'Report Preview - Calendar'
  | 'Report List - Open Report'
  | 'Report List - Create Report'
  | 'Report List - View'
  | 'Report - View';

export type SidebarEventTypes =
  | 'Sidebar - Home'
  | 'Sidebar - Bidders'
  | 'Sidebar - Channels'
  | 'Sidebar - Reporting'
  | 'Sidebar - Orders'
  | 'Sidebar - Line Items'
  | 'Sidebar - Creative Library'
  | 'Sidebar - Deals'
  | 'Sidebar - Audience Segments'
  | 'Sidebar - Contextual Segments'
  | 'Sidebar - SSP Analytics'
  | 'Sidebar - Routes'
  | 'Sidebar - IAB Category'
  | 'Sidebar - Network'
  | 'Sidebar - Reseller'
  | 'Sidebar - Freewheel Sites'
  | 'Sidebar - Brand Safety'
  | 'Sidebar - Configurations'
  | 'Sidebar - Monitors'
  | 'Sidebar - Inventory Forecast'
  | 'Sidebar - Inventory Rules'
  | 'Sidebar - SSAI Dashboard'
  | 'Sidebar - Measurement Dashboard'
  | 'Sidebar - Measurement Drill'
  | 'Sidebar - IAS Dashboard'
  | 'Sidebar - IAS Glossary'
  | 'Sidebar - Reports Dashboard'
  | 'Sidebar - IAS Help'
  | 'Sidebar - Brands Dashboard'
  | 'Sidebar - Return To Mobile View'
  | 'Sidebar - Return To Desktop View';

export type DashboardEventTypes =
  | 'Dashboard - Click Overview Tab'
  | 'Dashboard - Click Live Data Tab'
  | 'Dashboard - Run Agg Query'
  | 'Dashboard - View'
  | 'Dashboard - Run Live Query'
  | 'Dashboard - Click Key Metric'
  | 'Dashboard - Open Filter Box'
  | 'Dashboard - Open Calendar'
  | 'Dashboard - Open Compare Calendar'
  | 'Dashboard - Switch To Parent View'
  | 'Dashboard - Change Rows Per Channel Table'
  | 'Dashboard - Change Rows Per Demand Source Table'
  | 'Dashboard - Use Order Toggle on Channel Table'
  | 'Dashboard - Use Order Toggle on Demand Source Table'
  | 'Dashboard - Compare Enabled'
  | 'SSAI Dashboard - Run Agg Query'
  | 'SSAI Dashboard - Compare Enabled'
  | 'SSAI Dashboard - Open Calendar'
  | 'SSAI Dashboard - View'
  | 'SSAI Dashboard - Open Compare Calendar'
  | 'SSAI Dashboard - Open Filter Box'
  | 'Measurement Dashboard - Run Agg Query'
  | 'Measurement Dashboard - Compare Enabled'
  | 'Measurement Dashboard - Open Calendar'
  | 'Measurement Dashboard - View'
  | 'Measurement Dashboard - Open Compare Calendar'
  | 'Measurement Dashboard - Open Filter Box'
  | 'Measurement Dashboard - Click Inventory Data Tab'
  | 'Measurement Dashboard - Click Advertiser Data Tab'
  | 'IAS Publisher Dashboard - Run Agg Query'
  | 'IAS Publisher Dashboard - Compare Enabled'
  | 'IAS Publisher Dashboard - Open Calendar'
  | 'IAS Publisher Dashboard - View'
  | 'IAS Publisher Dashboard - Open Compare Calendar'
  | 'IAS Publisher Dashboard - Open Filter Pill'
  | 'IAS Publisher Dashboard - Click Overview Tab'
  | 'IAS Publisher Dashboard - Click Viewability Tab'
  | 'IAS Publisher Dashboard - Click Invalid Traffic Tab'
  | 'IAS Publisher Dashboard - Click Brand Safety Tab'
  | 'IAS Measurement Drill Dashboard - View'
  | 'IAS Measurement Drill Dashboard - Open Calendar'
  | 'IAS Measurement Drill Dashboard - Open Filter Pill'
  | 'IAS Measurement Drill Dashboard - Click Breadcrumb'
  | 'IAS Measurement Drill Dashboard - Run Table Agg Query'
  | 'IAS Measurement Drill Dashboard - Run Chart Agg Query';

export type BidderEventTypes =
  | 'Bidder List - View'
  | 'Bidder List - List Loaded'
  | 'Bidder List - List Loading Error'
  | 'Bidder List - View Group Tab'
  | 'Bidder List - View All Tab'
  | 'Bidder List - View Demand Source Settings Tab'
  | 'Bidder List - Create Bidder'
  | 'Bidder List - Bulk Upload'
  | 'Bidder List - Change Rows Per Page'
  | 'Bidder List - Search'
  | 'Bidder List - Open Bidder'
  | 'Bidder List - Click Active Toggle'
  | 'Bidder List - Modify Tier'
  | 'Bidder List - Clone Bidder'
  | 'Bidder List - Delete Bidder'
  | 'Bidder List - Use Channel Filter'
  | 'Bidder List - Use Priority Filter'
  | 'Bidder List - Use Status Filter'
  | 'Bidder Settings - View Bidder'
  | 'Bidder Settings - Live Data Loaded'
  | 'Bidder Settings - Hide Live Data'
  | 'Bidder Settings - Change Live Data Metrics'
  | 'Bidder Settings - Change Live Data Range'
  | 'Bidder Settings - Save Bidder'
  | 'Bidder Settings - Open Live Logs'
  | 'Bidder Settings - Search Live Logs';

export type LiveDataEventTypes =
  | 'Live Data - Live Data Loaded'
  | 'Live Data - Hide Live Data'
  | 'Live Data - Change Live Data Metrics'
  | 'Live Data - Change Live Data Range';

export type ChannelEventTypes =
  | 'Channel List - View'
  | 'Channel List - Channels List Loaded'
  | 'Channel List - Channel Groups List Loaded'
  | 'Channel List - View Group Tab'
  | 'Channel List - View All Tab'
  | 'Channel List - View Mapping Tab'
  | 'Channel List - Create Channel'
  | 'Channel List - Create Channel Group'
  | 'Channel List - Search Channels'
  | 'Channel List - Search Channel Groups'
  | 'Channel List - Search Routes'
  | 'Channel List - Create Route'
  | 'Channel List - Change Rows Per Page'
  | 'Channel List - Delete Channel'
  | 'Channel List - Delete Channel Group'
  | 'Channel List - Open Channel'
  | 'Channel List - Click Active Toggle'
  | 'Channel List - Use Status Filter'
  | 'Channel Settings - View Channel'
  | 'Channel Settings - View Details Tab'
  | 'Channel Settings - View Endpoint Tab'
  | 'Channel Settings - View Rewrites Tab'
  | 'Channel Settings - View Brand Safety Tab'
  | 'Channel Settings - Live Data Loaded'
  | 'Channel Settings - Change Live Data Metrics'
  | 'Channel Settings - Change Live Data Range'
  | 'Channel Settings - Live Data Hidden'
  | 'Channel Settings - Save Channel'
  | 'Channel Settings - Open Live Logs'
  | 'Channel Settings - Search Live Logs';

export type RouteEventTypes =
  | 'Route List - View'
  | 'Route List - Routes List Loaded'
  | 'Route List - Create Route'
  | 'Route List - Search Routes'
  | 'Route List - Change Rows Per Page'
  | 'Route List - Delete Route'
  | 'Route List - Open Route'
  | 'Route List - Click Active Toggle'
  | 'Route List - Use Status Filter'
  | 'Route Settings - View Route'
  | 'Route Settings - View Details Tab'
  | 'Route Settings - View Endpoint Tab'
  | 'Route Settings - Live Data Hidden'
  | 'Route Settings - Save Route';

export type ContextualSegmentEventTypes =
  | 'Contextual Segment List - View'
  | 'Contextual Segment List - Contextual Segments List Loaded'
  | 'Contextual Segment List - Create Contextual Segment'
  | 'Contextual Segment List - Search Contextual Segments'
  | 'Contextual Segment List - Change Rows Per Page'
  | 'Contextual Segment List - Delete Contextual Segment'
  | 'Contextual Segment List - Open Contextual Segment'
  | 'Contextual Segment List - Click Active Toggle'
  | 'Contextual Segment List - Use Status Filter'
  | 'Contextual Segment Settings - View Contextual Segment'
  | 'Contextual Segment Settings - View Details Tab'
  | 'Contextual Segment Settings - View Endpoint Tab'
  | 'Contextual Segment Settings - Live Data Hidden'
  | 'Contextual Segment Settings - Save Contextual Segment';

export type AudienceSegmentEventTypes =
  | 'Audience Manager - Selection Tree'
  | 'Audience Manager - Forecasting Tool'
  | 'Audience Manager - Forecasting Email';

export type CampaignForecastEventTypes =
  | 'Line Item - Forecasting Tool'
  | 'Line Item - Forecasting Email';

export type InventoryForecastEventTypes =
  | 'Inventory - Forecasting Tool'
  | 'Inventory - Forecasting Email';

export type MonitorEventTypes = 'Monitor List - Create New' | 'Monitor - Save';
